// ContactFirstPanel — the funnel's first step.
//
// The storefront used to open on the meter chooser, so somebody who left before
// the credit step left nothing behind: no name, no email, no way to follow up,
// and no way to even count the drop-off. Asking for contact details here means
// an abandoned signup is a lead an operator can call.
//
// ── WHAT THIS ASKS, AND WHAT IT POINTEDLY DOES NOT ────────────────────────
//
// Four fields. No date of birth, no SSN, no address, no consent checkbox, no
// one-time code. Those all stay at the credit step, behind the disclosures that
// justify asking for them. Putting any of them here would turn a "who are you"
// into an identity check before the shopper has seen a single price.
//
// ── THE SESSION ───────────────────────────────────────────────────────────
//
// The panel mints the funnel session itself (`useEnsureSessionQuery`), exactly
// as `ZipSubflow` and `EsiidSubflow` do. That is not ceremony: the lead is keyed
// on the funnel session id, which is what later steps use to find it again — so
// without a session here the credit step would create a SECOND lead for the same
// person. Minting it up front means the shopper never sees a session error and
// the two records can never diverge.

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody, Input, Label, Spinner } from '@fluxora/ui';
import { useState } from 'react';

import { postLeadContact } from '../api/contact.fetchers';
import { useEnsureSessionQuery } from '../api/serviceability.queries';
import {
  EMPTY_LEAD_CONTACT,
  formatPhoneInput,
  toLeadContactRequest,
  validateLeadContact,
  validateLeadContactField,
  type LeadContactDraft,
  type LeadContactErrors,
} from '../services/leadContact.service';

export interface ContactFirstPanelProps {
  /** Called once the lead is recorded, to advance to the meter chooser. */
  readonly onCaptured: () => void;
}

export function ContactFirstPanel({ onCaptured }: ContactFirstPanelProps): JSX.Element {
  const { t } = useTranslation();
  const session = useEnsureSessionQuery();

  const [draft, setDraft] = useState<LeadContactDraft>(EMPTY_LEAD_CONTACT);
  const [errors, setErrors] = useState<LeadContactErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const set = (field: keyof LeadContactDraft, value: string): void => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    // Clear this field's error as soon as it is edited. Leaving it on screen
    // while somebody fixes it reads as "still wrong" and is simply untrue.
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Validate a field as the shopper leaves it, so a letter in the phone number
  // is pointed out before they reach Continue — not only after. An EMPTY field
  // is left alone on blur: clicking into a field and out again is not a
  // mistake, and "Enter your phone number" before anything was typed reads as
  // nagging. Continue still reports every empty required field.
  const touch = (field: keyof LeadContactDraft): void => {
    if (draft[field].trim() === '') return;
    setErrors((prev) => ({ ...prev, [field]: validateLeadContactField(field, draft) }));
  };

  const handleSubmit = async (): Promise<void> => {
    const found = validateLeadContact(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    setFailed(false);
    try {
      await postLeadContact(toLeadContactRequest(draft));
      onCaptured();
    } catch {
      /* The lead did not record. We still let them shop — see the BFF route's
       * failure posture — but we say so rather than pretending, because the
       * alternative is silently losing the follow-up they just consented to. */
      setFailed(true);
      onCaptured();
    } finally {
      setSubmitting(false);
    }
  };

  const field = (
    id: keyof LeadContactDraft,
    label: string,
    type: string,
    placeholder: string,
    autoComplete: string,
  ): JSX.Element => (
    <div className="flex flex-col gap-1">
      {/* Every field is required; the star says so at a glance and `required`
          says so to assistive tech. The star sits BESIDE the label element, not
          inside it, so the label's text stays exactly the field name. */}
      <div className="flex items-center gap-0.5">
        <Label htmlFor={`contact-${id}`}>{label}</Label>
        <span aria-hidden="true" className="text-[13px] font-semibold leading-none text-[color:var(--color-danger,#dc2626)]">
          *
        </span>
      </div>
      <div className="relative">
        {/* The US country code is fixed, not typed: it sits INSIDE the field as
            a prefix, so the shopper enters the ten digits after it. Not the
            Input's addon slot — that renders a second bordered box beside the
            pill-shaped field and reads as two broken controls. */}
        {id === 'phone' ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted"
          >
            +1
          </span>
        ) : null}
        <Input
          id={`contact-${id}`}
          name={id}
          type={type}
          value={draft[id]}
          placeholder={placeholder}
          // The phone field gets no browser history dropdown: it covered the
          // field with every number ever typed here, prefix included.
          autoComplete={id === 'phone' ? 'off' : autoComplete}
          required
          aria-required="true"
          className={id === 'phone' ? 'pl-11' : undefined}
          hasError={errors[id] !== undefined}
          aria-invalid={errors[id] !== undefined}
          aria-describedby={errors[id] !== undefined ? `contact-${id}-error` : undefined}
          inputMode={id === 'phone' ? 'tel' : id === 'email' ? 'email' : 'text'}
          // `214 555 0134` is twelve characters; the mask never lets more in.
          maxLength={id === 'phone' ? 12 : id === 'email' ? 254 : 50}
          // The phone field keeps only digits and stops at ten, grouping them
          // as they are typed, so an invalid shape cannot sit in the field
          // looking accepted. Blur and Continue still check that the ten
          // digits are a number a US carrier could issue.
          onChange={(e) =>
            set(id, id === 'phone' ? formatPhoneInput(e.target.value, draft.phone) : e.target.value)
          }
          onBlur={() => touch(id)}
        />
      </div>
      {errors[id] !== undefined ? (
        <p
          id={`contact-${id}-error`}
          role="alert"
          className="text-[12px] text-[color:var(--color-danger,#dc2626)]"
        >
          {errors[id]}
        </p>
      ) : null}
    </div>
  );

  return (
    /* The card sits inside a 1.5px ring of the brand gradient in its PASTEL
       tints — the wrapper paints the gradient, the card covers all but its
       rim. This is the funnel's "you are here" surface; the tinted ring marks
       it without the full-strength stops cutting a hard line around it. */
    <div className="rounded-[calc(var(--r-lg)+1.5px)] bg-[image:var(--brand-gradient-tint)] p-[1.5px] shadow-card">
      <Card variant="glass" padding="md" className="!rounded-[var(--r-lg)]">
        <CardBody className="anim-stagger flex flex-col gap-4 p-0">
          <div className="flex flex-col gap-2">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">
              {t('shop.landing.contact.heading')}
            </h2>
          </div>

        {failed ? (
          <Alert variant="warning" title={t('shop.landing.contact.saveFailedTitle')}>
            {t('shop.landing.contact.saveFailedBody')}
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {field('firstName', t('shop.landing.contact.firstName'), 'text', 'Maria', 'given-name')}
          {field('lastName', t('shop.landing.contact.lastName'), 'text', 'Garcia', 'family-name')}
          {field('email', t('shop.landing.contact.email'), 'email', 'you@example.com', 'email')}
          {field('phone', t('shop.landing.contact.phone'), 'tel', '214 555 0134', 'tel')}
        </div>

        <Button
          variant="primary"
          shape="pill"
          fullWidth
          className="cta-sheen"
          // Disabled only while something is genuinely in flight. NOT disabled
          // on invalid input: a button that greys out without saying why is the
          // reason people abandon forms. Submit validates and points at the field.
          disabled={submitting || session.isPending}
          onClick={() => void handleSubmit()}
          data-testid="contact-first-submit"
        >
          {submitting ? <Spinner size="sm" /> : t('shop.landing.contact.cta')}
        </Button>

        <p className="text-[12px] leading-relaxed text-muted">
          {t('shop.landing.contact.notice')}
        </p>
      </CardBody>
      </Card>
    </div>
  );
}
